import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { createEgreso, deleteEgreso, getEgresos, updateEgreso } from '../../../js/egresosService';
import Paginacion from '../../../components/Reutilizables/Paginacion';
import { FaEdit, FaTrash } from 'react-icons/fa';
import ConfirmationModal from '../../../components/Reutilizables/ConfirmationModal';
import FetchWithGif from '../../../components/Reutilizables/FetchWithGif';

function formatMoney(value) {
  return Number(value || 0).toLocaleString('es-PE', { style: 'currency', currency: 'PEN' });
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-6 py-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="h-6 w-64 bg-gray-200 rounded mb-2 animate-pulse"></div>
          <div className="h-4 w-96 bg-gray-200 rounded animate-pulse"></div>
        </div>

        <section className="mb-6 bg-white shadow-lg rounded-xl p-6 border-t-4 border-gray-300">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i}>
                <div className="h-4 w-32 bg-gray-200 rounded mb-2 animate-pulse"></div>
                <div className="h-12 w-full bg-gray-200 rounded-lg animate-pulse"></div>
              </div>
            ))}
            <div className="md:col-span-2 h-12 w-full bg-gray-300 rounded-lg animate-pulse"></div>
            <div className="md:col-span-2 h-12 w-full bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
        </section>

        <section className="mb-6 bg-white shadow-lg rounded-xl p-6 border-t-4 border-gray-300">
          <div className="h-5 w-40 bg-gray-200 rounded mb-4 animate-pulse"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i}>
                <div className="h-4 w-28 bg-gray-200 rounded mb-2 animate-pulse"></div>
                <div className="h-12 w-full bg-gray-200 rounded-lg animate-pulse"></div>
              </div>
            ))}
            <div className="h-12 w-full bg-gray-300 rounded-lg animate-pulse"></div>
            <div className="h-12 w-full bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
        </section>

        <section className="bg-white shadow-lg rounded-xl p-6 border-t-4 border-gray-300">
          <div className="h-5 w-44 bg-gray-200 rounded mb-4 animate-pulse"></div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="font-semibold text-gray-700 bg-gray-50">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <th key={i} className="py-3 px-4 text-center">
                      <div className="h-4 w-20 bg-gray-200 rounded mx-auto animate-pulse"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {Array.from({ length: 6 }).map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {Array.from({ length: 5 }).map((_, colIndex) => (
                      <td key={colIndex} className="py-3 px-4 text-center">
                        <div className={`h-4 ${colIndex === 1 ? 'w-48' : 'w-20'} bg-gray-200 rounded mx-auto animate-pulse`}></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

export default function EgresosUI({ apiBasePath = '/api/admin', allowEditDelete = true }) {
  const [form, setForm] = useState({
    fecha_registro: '',
    descripcion: '',
    monto: '',
    abonado_por: 'CUENTA CORRIENTE',
  });
  const [filters, setFilters] = useState({
    fecha_inicio: '',
    fecha_fin: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmState, setConfirmState] = useState({ show: false, row: null });
  const [initialLoad, setInitialLoad] = useState(true);
  const [meta, setMeta] = useState({
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0,
    total: 0,
  });
  const [filterError, setFilterError] = useState('');

  const badgeClass = (abonadoPor) => {
    if (abonadoPor === 'CAJA CHICA') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (abonadoPor === 'CUENTA CORRIENTE') return 'bg-red-100 text-red-800 border-red-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const loadEgresos = useCallback(async (activeFilters, page = 1) => {
    setLoading(true);
    setError('');
    try {
      const response = await getEgresos({ ...(activeFilters || {}), page, per_page: 50 }, apiBasePath);
      if (!response.success) {
        throw new Error(response.message || 'Error al cargar egresos');
      }
      setItems(response.data || []);
      setMeta({
        current_page: response.meta?.current_page ?? 1,
        last_page: response.meta?.last_page ?? 1,
        from: response.meta?.from ?? 0,
        to: response.meta?.to ?? 0,
        total: response.meta?.total_registros ?? 0,
      });
    } catch (err) {
      setError(err?.message || 'No se pudieron cargar los egresos');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [apiBasePath]);

  useEffect(() => {
    loadEgresos({}, 1);
  }, [loadEgresos]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'descripcion') {
      setForm((prev) => ({ ...prev, [name]: value.toUpperCase() }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearForm = () => {
    setForm({ fecha_registro: '', descripcion: '', monto: '', abonado_por: 'CUENTA CORRIENTE' });
  };

  const handleApplyFilters = async () => {
    setFilterError('');
    if ((filters.fecha_inicio && !filters.fecha_fin) || (!filters.fecha_inicio && filters.fecha_fin)) {
      setFilterError('Selecciona fecha inicio y fecha fin para buscar.');
      return;
    }
    await loadEgresos(filters, 1);
  };

  const handleClearFilters = async () => {
    const cleared = { fecha_inicio: '', fecha_fin: '' };
    setFilters(cleared);
    setFilterError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        fecha_registro: form.fecha_registro,
        descripcion: form.descripcion,
        monto: Number(form.monto),
        abonado_por: form.abonado_por,
      };
      const response = allowEditDelete && editingId
        ? await updateEgreso(editingId, payload, apiBasePath)
        : await createEgreso(payload, apiBasePath);
      if (!response.success) {
        throw new Error(response.message || 'No se pudo guardar el egreso');
      }
      toast.success(editingId ? 'Egreso actualizado' : 'Egreso registrado');
      handleClearForm();
      setEditingId(null);
      await loadEgresos(filters, 1);
    } catch (err) {
      const message = err?.message || 'No se pudo guardar el egreso';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    if (!allowEditDelete) {
      return;
    }
    setEditingId(row.id);
    setForm({
      fecha_registro: row.fecha_registro,
      descripcion: row.descripcion,
      monto: row.monto,
      abonado_por: row.abonado_por || 'CUENTA CORRIENTE',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    handleClearForm();
  };

  const openDeleteConfirm = (row) => {
    if (!allowEditDelete) {
      return;
    }
    setConfirmState({ show: true, row });
  };

  const closeDeleteConfirm = () => {
    setConfirmState({ show: false, row: null });
  };

  const handleDelete = async () => {
    if (!allowEditDelete) {
      return;
    }
    const row = confirmState.row;
    if (!row) return;
    if (editingId === row.id) {
      cancelEdit();
    }
    setDeletingId(row.id);
    setError('');
    try {
      const response = await deleteEgreso(row.id, apiBasePath);
      if (!response.success) {
        throw new Error(response.message || 'No se pudo eliminar el egreso');
      }
      toast.success('Egreso eliminado');
      await loadEgresos(filters, meta.current_page || 1);
    } catch (err) {
      const message = err?.message || 'No se pudo eliminar el egreso';
      setError(message);
      toast.error(message);
    } finally {
      setDeletingId(null);
      closeDeleteConfirm();
    }
  };

  const rows = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const showActions = allowEditDelete;
  const showFetchOverlay = saving || deletingId !== null || (loading && !initialLoad);

  if (loading && initialLoad) return <LoadingSkeleton />;

  return (
    <div className="min-h-screen bg-[var(--surface-page)]">
      {showFetchOverlay && <FetchWithGif />}
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 md:px-6">
        <header className="ui-card relative overflow-hidden p-6">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_88%_10%,rgba(217,44,52,0.12),transparent_38%)]" />
          <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-red-600">Control financiero</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-900">Gestión de egresos</h1>
            <p className="mt-2 text-sm text-slate-600">
              Registra egresos y consulta el historial completo de movimientos de salida.
            </p>
          </div>
        </header>

        <section className="ui-card p-6">
          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label htmlFor="fecha_registro" className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                FECHA DE REGISTRO
              </label>
              <input
                id="fecha_registro"
                name="fecha_registro"
                type="date"
                value={form.fecha_registro}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200"
                required
              />
            </div>
            <div>
              <label htmlFor="descripcion" className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                DESCRIPCIÓN
              </label>
              <input
                id="descripcion"
                name="descripcion"
                type="text"
                value={form.descripcion}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200"
                placeholder="COMPRA DE MATERIALES DE OFICINA"
                required
              />
            </div>
            <div>
              <label htmlFor="monto" className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                MONTO (S/)
              </label>
              <input
                id="monto"
                name="monto"
                type="number"
                step="0.01"
                min="0"
                value={form.monto}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200"
                placeholder="100.00"
                required
              />
            </div>
            <div>
              <label htmlFor="abonado_por" className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                ABONADO POR
              </label>
              <select
                id="abonado_por"
                name="abonado_por"
                value={form.abonado_por}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200"
                required
              >
                <option value="CUENTA CORRIENTE">CUENTA CORRIENTE</option>
                <option value="CAJA CHICA">CAJA CHICA</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={saving}
              className="md:col-span-2 rounded-xl bg-red-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {saving ? 'GUARDANDO...' : (editingId ? 'ACTUALIZAR EGRESO' : 'REGISTRAR EGRESO')}
            </button>
            <button
              type="button"
              onClick={editingId ? cancelEdit : handleClearForm}
              disabled={saving}
              className="md:col-span-2 rounded-xl border border-red-500 bg-white px-6 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {editingId ? 'CANCELAR EDICIÓN' : 'LIMPIAR'}
            </button>
          </form>
        </section>

        <section className="ui-card p-6">
          <h2 className="mb-4 text-base font-semibold text-slate-900">Búsqueda por fecha</h2>
          {!!filterError && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {filterError}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label htmlFor="fecha_inicio" className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                FECHA INICIO
              </label>
              <input
                id="fecha_inicio"
                name="fecha_inicio"
                type="date"
                value={filters.fecha_inicio}
                onChange={handleFilterChange}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200"
              />
            </div>
            <div>
              <label htmlFor="fecha_fin" className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                FECHA FIN
              </label>
              <input
                id="fecha_fin"
                name="fecha_fin"
                type="date"
                value={filters.fecha_fin}
                onChange={handleFilterChange}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200"
              />
            </div>
            <button
              type="button"
              onClick={handleApplyFilters}
              disabled={loading}
              className="rounded-xl bg-red-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {loading ? 'BUSCANDO...' : 'BUSCAR'}
            </button>
            <button
              type="button"
              onClick={handleClearFilters}
              disabled={loading}
              className="rounded-xl border border-red-500 bg-white px-6 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70"
            >
              LIMPIAR
            </button>
          </div>
        </section>

        <section className="ui-card p-6">
          <h2 className="mb-4 text-base font-semibold text-slate-900">Listado de egresos</h2>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">
                  <th className="py-3 px-4 text-left">FECHA</th>
                  <th className="py-3 px-4 text-left">DESCRIPCIÓN</th>
                  <th className="py-3 px-4 text-center">MONTO</th>
                  <th className="py-3 px-4 text-center">ABONADO POR</th>
                  {showActions && <th className="py-3 px-4 text-center">ACCIONES</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={showActions ? 5 : 4} className="py-6 text-center text-slate-500">
                      No hay egresos registrados.
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm text-slate-700">
                      {formatDate(row.fecha_registro)}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-900">{row.descripcion}</td>
                    <td className="py-3 px-4 text-center text-sm font-semibold text-red-700">
                      {formatMoney(row.monto)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${badgeClass(row.abonado_por)}`}>
                        {row.abonado_por}
                      </span>
                    </td>
                    {showActions && (
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(row)}
                            className="inline-flex items-center gap-2 rounded-lg bg-amber-400 px-3 py-2 text-sm font-semibold text-amber-950 shadow-sm transition hover:bg-amber-500"
                            title="Editar"
                            aria-label="Editar egreso"
                          >
                            <FaEdit className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openDeleteConfirm(row)}
                            disabled={deletingId === row.id}
                            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                            title="Eliminar"
                            aria-label="Eliminar egreso"
                          >
                            <FaTrash className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {meta.last_page > 1 && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3">
              <Paginacion
                paginacion={{
                  pagina_actual: meta.current_page,
                  ultima_pagina: meta.last_page,
                  desde: meta.from,
                  hasta: meta.to,
                  total: meta.total,
                }}
                alCambiarPagina={(newPage) => loadEgresos(filters, newPage)}
                nombreItem="egresos"
                colorTema="red"
              />
            </div>
          )}
        </section>
      </div>

      <ConfirmationModal
        show={confirmState.show}
        title="¿Confirmar eliminacion del egreso?"
        message="¿Estas seguro de eliminar este egreso? Esta accion no se puede deshacer."
        onClose={closeDeleteConfirm}
        onConfirm={handleDelete}
        confirmText="Si, eliminar"
        cancelText="No, cancelar"
      />
    </div>
  );
}
